import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./HistoryPage.css";
import { auth } from "../firebase-config";
import { onAuthStateChanged } from "firebase/auth";
import tickerData from "../company_tickers.json";
import { Helmet } from "react-helmet";
import SearchBox from "./SearchBox";
import { FiTrash, FiX } from "react-icons/fi";

const FavoritesPage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const baseURL =
    process.env.REACT_APP_ENVIRONMENT_TYPE === "DEV"
      ? "http://127.0.0.1:5000"
      : "https://flask-backend-52245432644.us-central1.run.app";
  const frontendBaseURL =
    process.env.REACT_APP_ENVIRONMENT_TYPE === "DEV"
      ? "http://localhost:8080"
      : "https://marketcrunch.ai";

  const tickerLookup = Object.values(tickerData).reduce((acc, item) => {
    acc[item.ticker] = item.title
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
    return acc;
  }, {});

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUser(user);
      } else {
        setUser(null);
      }
    });
    return () => unsubscribe();
  }, [user]);

  useEffect(() => {
    const userId = localStorage.getItem("databaseUid");
    if (userId) {
      fetchFavorites(userId);
    } else {
      setError("User is not logged in");
      setLoading(false);
    }

    // Cleanup function
    return () => {
      // Optional: Clear cache if needed
      // const userId = localStorage.getItem("databaseUid");
      // if (userId) {
      //   localStorage.removeItem(`favoritesData_${userId}`);
      //   localStorage.removeItem(`favoritesDataTimestamp_${userId}`);
      // }
    };
  }, []);

  const handleRemoveFavorite = async (ticker) => {
    setLoading(true);
    const userId = localStorage.getItem("databaseUid");
    try {
      await axios.get(
        `${baseURL}/remove-from-favorites?uid=${userId}&ticker=${ticker}`
      );
      setError(null);
      // Clear cache when modifying favorites
      localStorage.removeItem(`favoritesData_${userId}`);
      localStorage.removeItem(`favoritesDataTimestamp_${userId}`);
      fetchFavorites(userId);
    } catch (err) {
      setError(`Failed to remove ${ticker} from favorites. Please try again.`);
      setLoading(false);
    }
  };

  const addToFavorites = async (ticker) => {
    setLoading(true);
    const userId = localStorage.getItem("databaseUid");
    try {
      await axios.get(
        `${baseURL}/add-to-favorites?uid=${userId}&ticker=${ticker}`
      );
      setError(null);
      // Clear cache when modifying favorites
      localStorage.removeItem(`favoritesData_${userId}`);
      localStorage.removeItem(`favoritesDataTimestamp_${userId}`);
      fetchFavorites(userId);
    } catch (err) {
      setError("Failed to add to favorites. Please try again.");
      setLoading(false);
    }
  };

  const fetchFavorites = async (userId) => {
    setLoading(true);
    try {
      // Try to get cached data first
      const cachedData = localStorage.getItem(`favoritesData_${userId}`);
      const cachedTimestamp = localStorage.getItem(
        `favoritesDataTimestamp_${userId}`
      );
      const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

      // If we have cached data and it's not too old, use it immediately
      if (cachedData && cachedTimestamp) {
        const isExpired =
          Date.now() - parseInt(cachedTimestamp) > CACHE_DURATION;
        if (!isExpired) {
          const parsedData = JSON.parse(cachedData);
          if (parsedData && Array.isArray(parsedData.favorites)) {
            setData(parsedData.favorites.reverse());
            setError(null);
            setLoading(false);
          }
        }
      }

      // Fetch fresh data
      const response = await axios.get(`${baseURL}/get-favorites`, {
        params: { uid: userId },
        headers: {
          "Content-Type": "application/json",
        },
      });
      const fetchedData = response.data;

      // Update cache
      localStorage.setItem(
        `favoritesData_${userId}`,
        JSON.stringify(fetchedData)
      );
      localStorage.setItem(
        `favoritesDataTimestamp_${userId}`,
        Date.now().toString()
      );

      if (fetchedData && Array.isArray(fetchedData.favorites)) {
        setData(fetchedData.favorites.reverse());
        setError(null);
      } else {
        setData([]);
        setError("You have no favorites! Add some to get started!");
      }
      setLoading(false);
    } catch (err) {
      setError("Failed to fetch favorites data. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="history-page-container">
      <Helmet>
        <title>Alerts - MarketCrunch AI</title>
        <meta
          name="description"
          content="Set alerts for your favorite stocks and get notified of important changes with MarketCrunch AI."
        />
      </Helmet>
      <div className="history-container">
        <div
          className="header-row"
          style={{
            paddingLeft: 0,
            marginTop: 0,
          }}
        >
          <div className="right-side-controls">
            <SearchBox
              buttonText={"Add"}
              onSearch={addToFavorites}
              isLanding={false}
            />
          </div>
        </div>

        <div className="history-table-container">
          <table className="history-table">
            <thead>
              <tr>
                <th>Company</th>
                <th>Ticker</th>
                <th>Current Price</th>
                <th></th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan="6"
                    style={{ textAlign: "center", fontFamily: "Inter" }}
                  >
                    Loading data...
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: "center" }}>
                    {error}
                  </td>
                </tr>
              ) : Array.isArray(data) && data.length === 0 ? (
                <tr>
                  <td
                    colSpan="6"
                    style={{ textAlign: "center", fontFamily: "Inter" }}
                  >
                    No alerts found
                  </td>
                </tr>
              ) : (
                Array.isArray(data) &&
                data.map((entry, index) => (
                  <tr key={index} style={{ fontFamily: "Inter" }}>
                    <td>{tickerLookup[entry[0]]}</td>
                    <td>{entry[0]}</td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      <span
                        style={{
                          fontFamily: "Inter",
                          backgroundColor:
                            parseFloat(entry[2]) > 0
                              ? "rgba(19, 156, 141, 0.075)"
                              : parseFloat(entry[2]) < 0
                              ? "rgba(255, 0, 0, 0.075)"
                              : "rgba(85, 85, 85, 0.075)",
                          color:
                            parseFloat(entry[2]) > 0
                              ? "#139C8D"
                              : parseFloat(entry[2]) < 0
                              ? "rgb(255, 76, 97)"
                              : "#555",
                          padding: "5px 10px",
                          borderRadius: "5px",
                        }}
                      >
                        {String(entry[1]) === "null" ||
                        String(entry[2]) === "null"
                          ? "Unable to fetch live data"
                          : `$${entry[1]} (${entry[2]}%)`}
                      </span>
                    </td>
                    <td>
                      <button
                        onClick={() => {
                          window.open(
                            `${frontendBaseURL}/analyze?t=${entry[0]}&source=favorites`
                          );
                        }}
                        style={{
                          padding: "7.5px 15px",
                          backgroundColor: "#139C8D",
                          border: 0,
                          borderRadius: 6,
                          color: "white",
                          fontFamily: "Inter",
                          fontSize: 14,
                          fontWeight: 600,
                          cursor: "pointer",
                        }}
                      >
                        Analyze
                      </button>
                    </td>
                    <td>
                      <button
                        style={{
                          border: 0,
                          backgroundColor: "transparent",
                          cursor: "pointer",
                          display: "flex",
                        }}
                        onClick={() => handleRemoveFavorite(entry[0])}
                      >
                        <FiTrash size={20} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default FavoritesPage;
